import { useState, useCallback, useEffect } from 'react'
import { ClaimSearchResult } from '../types/claim'
import { searchClaims, recentClaims } from '../services/claimsApi'
import { getApiErrorMessage } from '../utils/errorMessages'
import { useSsoToken } from './useSsoToken'

const MAX_SEARCH_RESULTS = 10
const MIN_SEARCH_LENGTH = 6
const NO_MIN_LENGTH_TYPES = new Set(['insuredName', 'broker', 'client', 'filename'])

function sortByDateOfLoss(claims: ClaimSearchResult[]): ClaimSearchResult[] {
  return [...claims].sort((a, b) => {
    const aTime = a.dateOfLoss ? new Date(a.dateOfLoss).getTime() : Number.NaN
    const bTime = b.dateOfLoss ? new Date(b.dateOfLoss).getTime() : Number.NaN
    const aValid = !Number.isNaN(aTime)
    const bValid = !Number.isNaN(bTime)
    if (!aValid && !bValid) return 0
    if (!aValid) return 1
    if (!bValid) return -1
    return bTime - aTime
  })
}

interface ClaimSearchState {
  query: string
  searchType: string
  results: ClaimSearchResult[]
  recent: ClaimSearchResult[]
  loading: boolean
  recentLoading: boolean
  error: string | null
  recentError: string | null
  hasSearched: boolean
}

export function useClaimSearch() {
  const { getToken } = useSsoToken()

  const [state, setState] = useState<ClaimSearchState>({
    query: '',
    searchType: 'reference',
    results: [],
    recent: [],
    loading: false,
    recentLoading: false,
    error: null,
    recentError: null,
    hasSearched: false,
  })

  const setQuery = useCallback((query: string) => {
    setState((prev) => ({ ...prev, query }))
  }, [])

  const setSearchType = useCallback((searchType: string) => {
    setState((prev) => ({ ...prev, searchType }))
  }, [])

  const loadRecentClaims = useCallback(async () => {
    setState((prev) => ({ ...prev, recentLoading: true, recentError: null }))

    try {
      const token = await getToken()
      const recent = sortByDateOfLoss(await recentClaims(token))

      setState((prev) => ({ ...prev, recent, recentLoading: false }))
    } catch (err) {
      console.error('[ClaimSearch] Failed to load recent claims:', err)
      const errorMessage = getApiErrorMessage(err, 'Unable to load recent claims. Please try searching.')
      setState((prev) => ({
        ...prev,
        recentLoading: false,
        recentError: errorMessage,
      }))
    }
  }, [getToken])

  const search = useCallback(async () => {
    const currentQuery = state.query.trim()

    if (currentQuery.length < MIN_SEARCH_LENGTH && !NO_MIN_LENGTH_TYPES.has(state.searchType)) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: `Please enter at least ${MIN_SEARCH_LENGTH} characters to search.`,
        results: [],
        hasSearched: true,
      }))
      return
    }

    setState((prev) => ({ ...prev, loading: true, error: null, results: [], hasSearched: true }))

    try {
      const token = await getToken()
      const allResults = await searchClaims(currentQuery, state.searchType, token)

      const results = sortByDateOfLoss(allResults).slice(0, MAX_SEARCH_RESULTS)

      setState((prev) => ({
        ...prev,
        results,
        loading: false,
        error: results.length === 0 ? `No claims found matching '${currentQuery}'. Try different criteria.` : null,
      }))
    } catch (err) {
      console.error('[ClaimSearch] Search failed:', err)
      const errorMessage = getApiErrorMessage(err, 'Unable to complete search. Please try again.')
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
        results: [],
      }))
    }
  }, [state.query, state.searchType, getToken])

  // Load recent claims on mount
  useEffect(() => {
    loadRecentClaims()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearSearch = useCallback(() => {
    setState((prev) => ({ ...prev, query: '', results: [], hasSearched: false, error: null }))
  }, [])

  return {
    query: state.query,
    searchType: state.searchType,
    results: state.results,
    recent: state.recent,
    loading: state.loading,
    recentLoading: state.recentLoading,
    error: state.error,
    recentError: state.recentError,
    hasSearched: state.hasSearched,
    setQuery,
    setSearchType,
    search,
    clearSearch,
    loadRecentClaims,
  }
}
